import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  ClickAwayListener,
  Grid,
  Typography,
  styled,
  alpha
} from '@mui/material';
import {
  Category as CategoryIcon
} from '@mui/icons-material';
import { Category, SubCategory } from '../../types/category';
import { getMainCategories, getSubcategories } from '../../services/categoryService';

// تنسيق مخصص للفئة الرئيسية
const CategoryButton = styled(Button)(({ theme }) => ({
  color: 'black',
  backgroundColor: '#fed430',
  padding: theme.spacing(1.5, 2.5),
  '&:hover': {
    backgroundColor: 'white',
    color: 'black', // إبقاء لون الخط أسود عند التحويم
    boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
  },
  '&:active': {
    backgroundColor: 'white',
    color: 'black' // إبقاء لون الخط أسود عند الضغط
  },
  transition: 'all 0.3s ease',
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '1rem',
  borderRadius: '0',
  height: '100%',
  minHeight: '64px',
  width: '100%',
  maxWidth: 'none' // إزالة الحد الأقصى للعرض
}));

// واجهة الخصائص للمكون
interface MegaMenuProps {
  onCategorySelect?: (categoryId: string) => void;
}

const MegaMenu: React.FC<MegaMenuProps> = ({ onCategorySelect }) => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [subcategories, setSubcategories] = useState<Record<string, SubCategory[]>>({});
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  // الحصول على الفئات الرئيسية عند تحميل المكون
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const mainCategories = await getMainCategories();

        // تصفية الفئات النشطة فقط
        const activeCategories = mainCategories.filter(cat => cat.isActive);
        setCategories(activeCategories);

        // الحصول على الفئات الفرعية لكل فئة رئيسية
        const subcatsMap: Record<string, SubCategory[]> = {};

        await Promise.all(
          activeCategories.map(async (category) => {
            const subs = await getSubcategories(category.id);
            // تصفية الفئات الفرعية النشطة فقط
            subcatsMap[category.id] = subs.filter(sub => sub.isActive);
          })
        );

        setSubcategories(subcatsMap);
      } catch (error) {
        console.error('Error fetching categories for mega menu:', error);
      }
    };

    fetchCategories();
  }, []);

  // فتح القائمة
  const handleToggle = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setOpen((prevOpen) => !prevOpen);
  };

  // إغلاق القائمة
  const handleClose = (event: Event | React.SyntheticEvent) => {
    if (anchorEl && anchorEl.contains(event.target as HTMLElement)) {
      return;
    }
    setOpen(false);
  };

  // اختيار فئة
  const handleCategorySelect = (categoryId: string) => {
    if (onCategorySelect) {
      onCategorySelect(categoryId);
    }
    setOpen(false);
  };

  return (
    <Box sx={{ position: 'relative' }}>
      <CategoryButton
        onClick={handleToggle}
        aria-controls={open ? 'mega-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
      >
        المنتجات
      </CategoryButton>

      {open && (
        <Box
          sx={{
            position: 'fixed',
            top: { xs: '56px', sm: '64px' },
            left: 0,
            right: 0,
            zIndex: 1200,
            width: '100%',
            boxShadow: '0 6px 20px rgba(0,0,0,0.12)'
          }}
        >
          <Paper
            elevation={3}
            sx={{
              width: '100%',
              borderRadius: 0,
              overflow: 'hidden',
              borderTop: '1px solid rgba(0,0,0,0.08)',
              borderBottom: '1px solid rgba(0,0,0,0.08)'
            }}
          >
            <ClickAwayListener onClickAway={handleClose}>
              <Box id="mega-menu" sx={{ p: 2, maxWidth: '1400px', mx: 'auto' }}>
                <Grid container spacing={2}>
                  {/* عرض الفئات الرئيسية في صف أفقي */}
                  <Grid item xs={12}>
                    <Box sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      flexWrap: 'wrap',
                      justifyContent: 'center',
                      gap: 2,
                      mb: 3,
                      pb: 2,
                      borderBottom: (theme) => `1px solid ${alpha(theme.palette.divider, 0.5)}`
                    }}>
                      {/* زر عرض جميع الفئات */}
                      <Box
                        onClick={() => {
                          setOpen(false);
                          window.location.href = '/categories';
                        }}
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          p: 1.5,
                          borderRadius: 2,
                          cursor: 'pointer',
                          width: { xs: '80px', sm: '100px', md: '120px' },
                          transition: 'all 0.3s ease',
                          backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1),
                          '&:hover': {
                            backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.2),
                            transform: 'translateY(-5px)'
                          }
                        }}
                      >
                        <Box
                          sx={{
                            width: { xs: 40, sm: 50, md: 60 },
                            height: { xs: 40, sm: 50, md: 60 },
                            borderRadius: '50%',
                            mb: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.2)
                          }}
                        >
                          <CategoryIcon sx={{ color: 'primary.main', fontSize: { xs: 24, sm: 28, md: 32 } }} />
                        </Box>
                        <Typography
                          sx={{
                            fontWeight: 600,
                            fontSize: { xs: '0.75rem', sm: '0.85rem', md: '0.9rem' },
                            textAlign: 'center',
                            maxWidth: '100%',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            color: 'primary.main'
                          }}
                        >
                          جميع الفئات
                        </Typography>
                      </Box>

                      {categories.map((category) => (
                        <Box
                          key={category.id}
                          onClick={() => {
                            handleCategorySelect(category.id);
                            window.location.href = `/category/${category.id}`;
                          }}
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            p: 1.5,
                            borderRadius: 2,
                            cursor: 'pointer',
                            width: { xs: '80px', sm: '100px', md: '120px' },
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              backgroundColor: 'rgba(0,0,0,0.04)',
                              transform: 'translateY(-5px)'
                            }
                          }}
                        >
                          {category.imageUrl ? (
                            <Box
                              component="img"
                              src={category.imageUrl}
                              alt={category.name}
                              sx={{
                                width: { xs: 40, sm: 50, md: 60 },
                                height: { xs: 40, sm: 50, md: 60 },
                                borderRadius: '50%',
                                mb: 1,
                                border: '2px solid rgba(0,0,0,0.08)',
                                boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                              }}
                            />
                          ) : (
                            <Box
                              sx={{
                                width: { xs: 40, sm: 50, md: 60 },
                                height: { xs: 40, sm: 50, md: 60 },
                                borderRadius: '50%',
                                mb: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1)
                              }}
                            >
                              <CategoryIcon sx={{ color: 'primary.main', fontSize: { xs: 24, sm: 28, md: 32 } }} />
                            </Box>
                          )}
                          <Typography
                            sx={{
                              fontWeight: 500,
                              fontSize: { xs: '0.75rem', sm: '0.85rem', md: '0.9rem' },
                              textAlign: 'center',
                              maxWidth: '100%',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {category.name}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Grid>

                  {/* عرض الفئات الفرعية في صفوف أفقية */}
                  <Grid item xs={12}>
                    <Grid container spacing={2}>
                      {categories.map((category) => (
                        subcategories[category.id]?.length > 0 && (
                          <Grid item xs={12} sm={6} md={4} lg={3} key={`sub-${category.id}`}>
                            <Box sx={{
                              p: 2,
                              borderRadius: 2,
                              transition: 'all 0.3s ease',
                              height: '100%',
                              border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                              '&:hover': {
                                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                                borderColor: (theme) => alpha(theme.palette.primary.main, 0.2)
                              }
                            }}>
                              <Typography
                                variant="subtitle2"
                                onClick={() => {
                                  handleCategorySelect(category.id);
                                  window.location.href = `/category/${category.id}`;
                                }}
                                sx={{
                                  fontWeight: 'bold',
                                  mb: 2,
                                  display: 'block',
                                  color: 'primary.main',
                                  textDecoration: 'none',
                                  cursor: 'pointer',
                                  position: 'relative',
                                  paddingBottom: 1,
                                  '&:after': {
                                    content: '""',
                                    position: 'absolute',
                                    bottom: 0,
                                    right: 0,
                                    width: '30px',
                                    height: '2px',
                                    backgroundColor: 'primary.main'
                                  },
                                  '&:hover': {
                                    color: 'primary.dark',
                                    '&:after': {
                                      width: '50px'
                                    }
                                  }
                                }}
                              >
                                {category.name}
                              </Typography>
                              <Box sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 1
                              }}>
                                {subcategories[category.id]?.map((subcat) => (
                                  <Box
                                    key={subcat.id}
                                    onClick={() => {
                                      handleCategorySelect(subcat.id);
                                      window.location.href = `/category/${subcat.id}`;
                                    }}
                                    sx={{
                                      py: 0.5,
                                      px: 1.5,
                                      borderRadius: '20px',
                                      backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.05),
                                      cursor: 'pointer',
                                      transition: 'all 0.2s ease',
                                      '&:hover': {
                                        backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1),
                                        transform: 'translateY(-2px)'
                                      }
                                    }}
                                  >
                                    <Typography variant="body2" sx={{ fontWeight: 400 }}>
                                      {subcat.name}
                                    </Typography>
                                  </Box>
                                ))}
                              </Box>
                            </Box>
                          </Grid>
                        )
                      ))}
                    </Grid>
                  </Grid>
                </Grid>
              </Box>
            </ClickAwayListener>
          </Paper>
        </Box>
      )}
    </Box>
  );
};

export default MegaMenu;
